import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Download, Save, RefreshCw, Database, HardDriveDownload, Trash2, ShieldAlert } from "lucide-react";

const AdminBackup = () => {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const load = () => api.get("/backup/list").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/backup/create");
      toast.success("Backup berhasil dibuat");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal"); }
    finally { setCreating(false); }
  };

  const restore = async (id) => {
    setRestoring(id);
    try {
      const { data } = await api.post(`/backup/restore/${id}`);
      toast.success(`Restore selesai: ${Object.values(data.restored).reduce((a,b)=>a+b,0)} dokumen dipulihkan`);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal restore"); }
    finally { setRestoring(null); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus backup ini?")) return;
    await api.delete(`/media/${id}`);
    toast.success("Backup dihapus"); load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold">Backup & Restore</h1>
          <p className="mt-1 text-sm text-zinc-500">Backup semua konten website (produk, artikel, galeri, pengaturan, dll). Simpan berkala untuk keamanan.</p>
        </div>
        <button onClick={create} disabled={creating} data-testid="backup-create" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-maroon-800 text-white font-semibold hover:bg-maroon-900 disabled:opacity-60">
          {creating ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
          {creating ? "Membuat backup..." : "Buat Backup Sekarang"}
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-start gap-3">
        <ShieldAlert className="text-amber-700 shrink-0" size={20} />
        <div className="text-sm text-amber-900">
          <strong>Penting:</strong> Restore akan <strong>menimpa</strong> data konten (produk, artikel, galeri, FAQ, testimoni, pages, leads, settings, media). Kredensial admin (password) TIDAK akan diubah untuk keamanan.
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="p-4">File</th>
              <th className="p-4">Tanggal</th>
              <th className="p-4">Ukuran</th>
              <th className="p-4">Konten</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} className="border-t border-zinc-100">
                <td className="p-4">
                  <div className="flex items-center gap-2 text-zinc-900 font-medium">
                    <Database size={14} className="text-maroon-700" />
                    <span className="font-mono text-xs">{b.original_filename}</span>
                  </div>
                </td>
                <td className="p-4 text-zinc-500 text-xs">{new Date(b.created_at).toLocaleString("id-ID")}</td>
                <td className="p-4 text-zinc-600 text-xs">{(b.size / 1024).toFixed(1)} KB</td>
                <td className="p-4 text-zinc-500 text-xs">
                  {b.counts && Object.entries(b.counts).map(([k, v]) => v > 0 && (
                    <span key={k} className="inline-block mr-2">{k}:{v}</span>
                  ))}
                </td>
                <td className="p-4 text-right">
                  <a href={b.url} target="_blank" rel="noopener noreferrer" className="inline-flex p-2 rounded-lg hover:bg-zinc-100" title="Download">
                    <Download size={14} />
                  </a>
                  <button onClick={() => restore(b.id)} disabled={restoring === b.id} data-testid={`backup-restore-${b.id}`} className="inline-flex p-2 rounded-lg hover:bg-amber-50 text-amber-700 disabled:opacity-40" title="Restore">
                    {restoring === b.id ? <RefreshCw size={14} className="animate-spin" /> : <HardDriveDownload size={14} />}
                  </button>
                  <button onClick={() => del(b.id)} className="inline-flex p-2 rounded-lg hover:bg-red-50 text-red-600" title="Hapus">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-16 text-center text-zinc-500 text-sm">Belum ada backup. Klik "Buat Backup Sekarang" untuk mulai.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminBackup;
